import { useState, Dispatch, SetStateAction, useCallback } from 'react';
import { useLocalStorageBackupStore } from './useLocalStorageBackupStore';
import _ from 'lodash';
import { InteractionStepType } from '@scale/shared/types/chat';

// Backups should last 24 hours.
const maxDuration = 24 * 60 * 60 * 1000;

export const getLocalStorageKey = ({
  assignmentId,
  stepType,
  responseIndex,
  disabled,
  chatIndex,
}: {
  assignmentId?: string;
  stepType: InteractionStepType;
  responseIndex: number;
  disabled?: boolean;
  chatIndex?: number;
}) => {
  if (_.isNil(assignmentId) || disabled) {
    return undefined;
  }

  const key = `${assignmentId}-${stepType}-${responseIndex}`;
  return chatIndex ? `${key}-${chatIndex}` : key;
};

type UseLocalStorageWithExpiryReturnType<T> = [T, Dispatch<SetStateAction<T>>, () => void];

const useLocalStorageWithExpiry = <T>(
  initialValue: T,
  key?: string,
  disableAutomaticClearing?: boolean,
): UseLocalStorageWithExpiryReturnType<T> => {
  const setBackup = useLocalStorageBackupStore(state => state.setBackup);
  const clearBackup = useLocalStorageBackupStore(state => state.clearBackup);
  const getBackup = useLocalStorageBackupStore(state => state.getBackup);

  // Return the backup value if it exists and is non-expired, otherwise return the initial value
  const [value, setValue] = useState<T>(() => {
    if (_.isNil(key)) {
      return initialValue;
    }

    const backup = getBackup(key);
    if (_.isNil(backup)) {
      return initialValue;
    }
    if (backup.expired < Date.now()) {
      clearBackup(key);
      return initialValue;
    }

    return backup.value;
  });

  const setNewValue = useCallback(
    (value: any) => {
      setValue(value);
      if (_.isNil(key) || _.isNil(value)) {
        return;
      }

      // If the value is equal to the initial value, we can clear the backup
      if (_.isEqual(value, initialValue) && !disableAutomaticClearing) {
        clearBackup(key);
        return;
      }

      setBackup(key, value, Date.now() + maxDuration);
    },
    [key, setBackup, clearBackup, disableAutomaticClearing, initialValue],
  );

  const clearBackupValue = useCallback(() => {
    if (!_.isNil(key)) {
      clearBackup(key);
    }
  }, [clearBackup, key]);

  return [value, setNewValue, clearBackupValue];
};

export default useLocalStorageWithExpiry;
